import type {KeyboardEvent, ReactElement} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Controller, FormProvider, useForm, useWatch} from 'react-hook-form';
import type {ButtonAppearance} from '@Components/button';
import {Button, IconPlacement, ButtonSize, ButtonType, ButtonVariant} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import {AiPromptBoxAttachments} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {convertAttachmentsToUserRequestParts} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import {getDefaultAttachments} from '@Components/ai-chat-window/lib/ai-chat-prompt.library';
import {resolveChatMediaUrl} from '@Libraries/ai-chat.library';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {MediaItemSizeType} from '@Libraries/s3-library';
import type {ChatMessagePart} from '@Components/ai-chat-window/ai-chat-window.types';
import type {SurveyFormValues, SurveyProps} from './survey.types';
import {SurveyFieldKind} from './survey.types';
import {buildResponse, emptyValueForFieldKind, otherKey, presentAnswer, presentText} from './survey.library';
import {FieldRenderer} from './fields/field-renderer';
import {AiChatTestId} from '@/test-ids/ai-chat-test-id';
import styles from './survey.module.scss';

export function Survey({spec, onSubmit, onCancel, isSubmitting = false}: SurveyProps): ReactElement {
  const hasSubmittedRef = useRef(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const isLocked = isSubmitting || hasSubmitted;
  const methods = useForm<SurveyFormValues>({
    mode: 'onChange',
    shouldUnregister: false,
  });
  const [stepIndex, setStepIndex] = useState(0);
  const {isSmall} = useAiChatSize();

  const footerRef = useRef<HTMLDivElement>(null);
  const fieldSlotRef = useRef<HTMLDivElement>(null);
  const promptBoxId = `survey-${spec.surveyId}`;
  const promptBoxConfig = useMemo(() => ({size: AiPromptBoxSize.MEDIUM, attachments: getDefaultAttachments()}), []);
  const surveyPromptBox = useAiPromptBox(promptBoxId, promptBoxConfig);
  const totalSteps = spec.steps.length;
  const currentStep = spec.steps[stepIndex];
  const isLastStep = stepIndex === totalSteps - 1;
  const isFirstStep = stepIndex === 0;
  const isMultiStep = totalSteps > 1;
  const isTextStep = currentStep.field.kind === SurveyFieldKind.TEXT;

  useEffect(() => {
    const slot = fieldSlotRef.current;

    if (slot === null) {
      return;
    }

    if (!isTextStep) {
      slot.focus();
      return;
    }

    const focusable = slot.querySelector<HTMLElement>('input, textarea');

    if (focusable === null) {
      slot.focus();
      return;
    }

    focusable.focus();
  }, [stepIndex, isTextStep]);

  const watchedValue = useWatch({control: methods.control, name: currentStep.key});
  const watchedOther = useWatch({control: methods.control, name: otherKey(currentStep.key)});
  const hasAnswer = presentAnswer(watchedValue) !== undefined || presentText(watchedOther) !== undefined;
  const isActionDisabled = currentStep.required !== false && !hasAnswer;

  const buildAttachmentParts = (): ChatMessagePart[] => convertAttachmentsToUserRequestParts(surveyPromptBox.state.attachments?.items ?? []);

  const advanceOrSubmit = (): void => {
    if (isLastStep) {
      if (hasSubmittedRef.current) {
        return;
      }

      hasSubmittedRef.current = true;
      setHasSubmitted(true);
      onSubmit(buildResponse(methods.getValues(), spec.steps), buildAttachmentParts());

      return;
    }

    setStepIndex((i) => i + 1);
  };

  const goNext = async (): Promise<void> => {
    const valid = await methods.trigger(currentStep.key);

    if (!valid) {
      return;
    }

    advanceOrSubmit();
  };

  const submitIfAllowed = (): void => {
    if (isActionDisabled || isLocked) {
      return;
    }

    void goNext();
  };

  /** Enter submits the step the way the footer button does; Shift+Enter stays a newline, and mobile keeps Enter as one. */
  const handleFieldKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!isTextStep || event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing || window.PMW.isMobileOS()) {
      return;
    }

    event.preventDefault();

    submitIfAllowed();
  };

  const goBack = (): void => {
    if (isFirstStep) {
      return;
    }
    setStepIndex((i) => i - 1);
  };

  const skipStep = (): void => {
    methods.resetField(currentStep.key);
    methods.resetField(otherKey(currentStep.key));
    advanceOrSubmit();
  };

  const renderStepImage = (): ReactElement | null => {
    if (currentStep.imageUrl === undefined || currentStep.imageUrl === '') {
      return null;
    }
    return (
      <div className={styles.stepImage}>
        <img className={styles.stepImageImg} src={resolveChatMediaUrl(currentStep.imageUrl, MediaItemSizeType.THUMB)} alt="" />
      </div>
    );
  };

  const renderStepper = (): ReactElement | null => {
    if (!isMultiStep) {
      return null;
    }
    return (
      <div className={styles.stepper}>
        <Button
          size={ButtonSize.SMALL}
          type={ButtonType.TERTIARY}
          icon="icon-caret-left"
          customClasses={styles.navBtn}
          title={window.i18next.t('pmwjs_back')}
          disabled={isFirstStep || isLocked}
          onClick={goBack}
        />
        <Text size={TextSize.SMALL} val={`${stepIndex + 1} / ${totalSteps}`} className={styles.stepCount} attributes={{role: 'status'}} />
        <Button
          size={ButtonSize.SMALL}
          type={ButtonType.TERTIARY}
          icon="icon-caret-right"
          customClasses={styles.navBtn}
          title={window.i18next.t('pmwjs_next')}
          disabled={isLastStep || isLocked}
          onClick={(): void => {
            void goNext();
          }}
        />
      </div>
    );
  };

  const renderAttachments = (): ReactElement | null => {
    if (!isTextStep) {
      return null;
    }
    return <AiPromptBoxAttachments id={promptBoxId} />;
  };

  const renderFooterLeft = (): ReactElement => {
    if (isTextStep) {
      // Open-ended step: the Ai prompt box uploads control (same circular "+" + full upload dropdown),
      // action buttons pushed right.
      return (
        <>
          <AiPromptBoxUploads promptBoxId={promptBoxId} footerRef={footerRef} renderAttachmentTooltip />
          <div className={styles.spacer} />
        </>
      );
    }
    const placeholder = window.i18next.t('pmwjs_something_else');
    return (
      <Controller
        control={methods.control}
        name={otherKey(currentStep.key)}
        defaultValue=""
        render={({field: rhfField}): ReactElement => (
          <InputField
            className={styles.somethingElse}
            inputClassName="_borderless"
            iconClassName={styles.somethingElseIcon}
            hasIcon="icon-pencil"
            size={InputFieldSize.SMALL}
            placeholder={placeholder}
            elementTitle={placeholder}
            input={typeof rhfField.value === 'string' ? rhfField.value : ''}
            isDisabled={isLocked}
            onInputSubmit={submitIfAllowed}
            onInputChange={(val): void => {
              const text = String(val);
              rhfField.onChange(text);
              if (text !== '') {
                methods.setValue(currentStep.key, emptyValueForFieldKind(currentStep.field.kind), {shouldValidate: true});
              }
            }}
            onInputBlur={(): void => {
              rhfField.onBlur();
            }}
          />
        )}
      />
    );
  };

  const contentClasses = [styles.content, isTextStep && styles.contentFlush].filter(Boolean).join(' ');

  const getNextStepButtonAppearance = (): ButtonAppearance => {
    if (isLastStep) {
      return {type: ButtonType.PRIMARY, variant: ButtonVariant.AI};
    }
    return {type: ButtonType.PRIMARY};
  };

  return (
    <FormProvider {...methods}>
      <div className={isSmall ? `${styles.root} ${styles.small}` : styles.root}>
        {renderStepImage()}
        <div className={styles.header}>
          <Text size={TextSize.SMALL} val={currentStep.helpText} className={styles.label} />
          <div className={styles.headerControls}>
            {renderStepper()}
            <Button
              size={ButtonSize.SMALL}
              type={ButtonType.TERTIARY}
              customClasses="spacing-m-r-0 spacing-p-2"
              icon="icon-close"
              title={window.i18next.t('pmwjs_cancel')}
              disabled={isLocked}
              onClick={onCancel}
            />
          </div>
        </div>

        <div className={contentClasses}>
          {renderAttachments()}
          <div key={currentStep.key} ref={fieldSlotRef} tabIndex={-1} className={styles.fieldSlot} onKeyDown={handleFieldKeyDown}>
            <FieldRenderer step={currentStep} />
          </div>
        </div>

        <div className={styles.footer} ref={footerRef}>
          {renderFooterLeft()}
          <div className={styles.footerRight}>
            <Button size={ButtonSize.MEDIUM} type={ButtonType.TERTIARY} text={window.i18next.t('pmwjs_skip')} disabled={isLocked} onClick={skipStep} />
            <Button
              size={ButtonSize.MEDIUM}
              {...getNextStepButtonAppearance()}
              iconPlacement={IconPlacement.RIGHT}
              icon={isLastStep ? "icon-caret-right" : "icon-arrow-right"}
              text={isLastStep ? (spec.submitLabel ?? window.i18next.t('pmwjs_done')) : window.i18next.t('pmwjs_next')}
              data-testid={AiChatTestId.SURVEY_SUBMIT}
              disabled={isActionDisabled || isLocked}
              onClick={(): void => {
                void goNext();
              }}
              isLoading={isLocked}
            />
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
